import { FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useCareerOpening, useSubmitCareerApplication } from '../hooks/useCareers';

const BRAND_YELLOW = '#E0A15E';

const Page = styled.main`
  min-height: 100vh;
  padding: calc(84px + env(safe-area-inset-top)) 1.1rem 2rem;
  background:
    radial-gradient(1200px 720px at 18% 8%, rgba(56, 189, 248, 0.32) 0%, rgba(56, 189, 248, 0) 62%),
    radial-gradient(1100px 760px at 84% 24%, rgba(168, 85, 247, 0.34) 0%, rgba(168, 85, 247, 0) 60%),
    radial-gradient(1200px 740px at 20% 50%, rgba(244, 114, 182, 0.3) 0%, rgba(244, 114, 182, 0) 62%),
    radial-gradient(1050px 700px at 82% 74%, rgba(251, 146, 60, 0.28) 0%, rgba(251, 146, 60, 0) 60%),
    radial-gradient(1200px 760px at 26% 94%, rgba(45, 212, 191, 0.3) 0%, rgba(45, 212, 191, 0) 64%),
    linear-gradient(180deg, #efecf0 0%, #efecf0 100%);
  color: #17212f;
`;

const Container = styled.div`
  width: min(1200px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 0.95rem;
`;

const BackLink = styled(Link)`
  text-decoration: none;
  width: fit-content;
  border-radius: 999px;
  border: 1px solid rgba(23, 33, 47, 0.14);
  color: #17212f;
  background: rgba(255, 255, 255, 0.42);
  padding: 0.45rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
`;

const Layout = styled.div`
  display: grid;
  gap: 0.95rem;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  border-radius: 16px;
  // border: 1px solid rgba(255, 255, 255, 0.14);
  // background: rgba(255, 255, 255, 0.06);
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
`;

const JobTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.6rem, 3.4vw, 2.5rem);
  line-height: 1.05;
  color: #17212f;
`;

const Meta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #5f6b7a;
`;

const BodyText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #465263;
`;

const Subheading = styled.h2`
  margin: 0.2rem 0 0;
  font-size: 1rem;
  color: #17212f;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: 0.35rem;

  li {
    font-size: 0.9rem;
    color: #465263;
    line-height: 1.45;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 0.68rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 0.55rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 0.32rem;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 800;
  color: #5f6b7a;
`;

const ConsentField = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  color: #334155;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.35;
`;

const ConsentCheckbox = styled.input`
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: ${BRAND_YELLOW};
`;

const glassFieldCss = css`
  border: 1px solid rgba(23, 33, 47, 0.14);
  background: rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(14px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    0 8px 18px rgba(23, 33, 47, 0.05);
  color: #17212f;

  &::placeholder {
    color: #7b8794;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.8);
    background: rgba(255, 255, 255, 0.34);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.58),
      0 10px 22px rgba(23, 33, 47, 0.07);
  }
`;

const Input = styled.input`
  min-height: 40px;
  border-radius: 10px;
  padding: 0 11px;
  font-size: 0.9rem;
  ${glassFieldCss};
`;

const Select = styled.select`
  min-height: 40px;
  border-radius: 10px;
  padding: 0 11px;
  font-size: 0.9rem;
  ${glassFieldCss};

  option {
    color: #17212f;
    background: #efecf0;
  }
`;

const Textarea = styled.textarea`
  min-height: 110px;
  border-radius: 10px;
  padding: 10px 11px;
  font-size: 0.9rem;
  resize: vertical;
  font-family: inherit;
  ${glassFieldCss};
`;

const FileInput = styled.input`
  min-height: 40px;
  border-radius: 10px;
  border: 1px dashed rgba(23, 33, 47, 0.28);
  background: rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(14px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.45),
    0 8px 18px rgba(23, 33, 47, 0.05);
  color: #17212f;
  padding: 8px 10px;
  font-size: 0.86rem;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #ffb4b4;
  font-size: 0.82rem;
  font-weight: 700;
`;

const Submit = styled.button`
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(224, 161, 94, 0.95);
  background: ${BRAND_YELLOW};
  color: #111625;
  font-size: 0.9rem;
  font-weight: 900;
  cursor: pointer;
`;

type ApplicationFormState = {
  firstName: string;
  lastName: string;
  email: string;
  contactPhone: string;
  wasReferred: 'no' | 'yes';
  referredBy: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  backgroundCheckAcknowledged: boolean;
  linkedInUrl: string;
  portfolioUrl: string;
  coverLetterText: string;
  resumeFile: File | null;
  coverLetterFile: File | null;
};

const DEFAULT_FORM: ApplicationFormState = {
  firstName: '',
  lastName: '',
  email: '',
  contactPhone: '',
  wasReferred: 'no',
  referredBy: '',
  addressLine1: '',
  city: '',
  state: '',
  postalCode: '',
  backgroundCheckAcknowledged: false,
  linkedInUrl: '',
  portfolioUrl: '',
  coverLetterText: '',
  resumeFile: null,
  coverLetterFile: null,
};

function validate(form: ApplicationFormState): string | null {
  if (!form.firstName.trim()) return 'First name is required.';
  if (!form.lastName.trim()) return 'Last name is required.';
  if (!form.email.trim()) return 'Email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Enter a valid email address.';
  if (!form.addressLine1.trim()) return 'Address is required.';
  if (!form.city.trim()) return 'City is required.';
  if (!form.state.trim()) return 'State is required.';
  if (!form.postalCode.trim()) return 'Zip code is required.';
  if (!form.backgroundCheckAcknowledged) {
    return 'You must acknowledge the background check requirement.';
  }
  if (form.wasReferred === 'yes' && !form.referredBy.trim()) {
    return 'Please share who referred you.';
  }
  if (!form.resumeFile) return 'Resume upload is required.';
  if (!form.coverLetterFile) return 'Cover letter upload is required.';
  return null;
}

export default function CareerJobPage() {
  const { jobSlug } = useParams<{ jobSlug: string }>();
  const navigate = useNavigate();
  const { job, isLoading } = useCareerOpening(jobSlug);
  const submitApplication = useSubmitCareerApplication();
  const [form, setForm] = useState<ApplicationFormState>(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!job) return;
    const currentError = validate(form);
    if (currentError) {
      setError(currentError);
      return;
    }

    setError(null);

    try {
      await submitApplication.mutateAsync({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        contactPhone: form.contactPhone,
        wasReferred: form.wasReferred === 'yes',
        referredBy: form.referredBy,
        addressLine1: form.addressLine1,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        backgroundCheckAcknowledged: form.backgroundCheckAcknowledged,
        linkedInUrl: form.linkedInUrl,
        portfolioUrl: form.portfolioUrl,
        coverLetterText: form.coverLetterText,
        resumeFile: form.resumeFile as File,
        coverLetterFile: form.coverLetterFile as File,
        job,
      });

      navigate(`/careers/${job.slug}/thank-you`, {
        replace: true,
        state: {
          jobTitle: job.title,
          requisitionId: job.requisitionId,
        },
      });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // Keep detailed diagnostics out of public UI.
        // eslint-disable-next-line no-console
        console.error('Career application submit failed:', err);
      }
      setError('We could not submit your application right now. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Page>
        <Container>
          <BackLink to="/careers">Back to careers</BackLink>
          <Card>
            <BodyText>Loading job...</BodyText>
          </Card>
        </Container>
      </Page>
    );
  }

  if (!job) {
    return (
      <Page>
        <Container>
          <BackLink to="/careers">Back to careers</BackLink>
          <Card>
            <JobTitle>Role not found</JobTitle>
            <BodyText>This role may no longer be published.</BodyText>
          </Card>
        </Container>
      </Page>
    );
  }

  return (
    <Page>
      <Container>
        <BackLink to="/careers">Back to careers</BackLink>

        <Layout>
          <Card>
            <JobTitle>{job.title}</JobTitle>
            <Meta>
              {job.requisitionId} • {job.team} • {job.location} • {job.workType.replace('_', ' ')}
            </Meta>
            <BodyText>{job.summary}</BodyText>

            <Subheading>Job description</Subheading>
            <BodyText>{job.description}</BodyText>

            <Subheading>Responsibilities</Subheading>
            <List>
              {job.responsibilities.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </List>

            <Subheading>Requirements</Subheading>
            <List>
              {job.requirements.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </List>
          </Card>

          <Card>
            <Subheading>Application form</Subheading>
            <Form onSubmit={onSubmit}>
              <Grid>
                <Field>
                  First name
                  <Input
                    value={form.firstName}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, firstName: event.target.value }))
                    }
                  />
                </Field>
                <Field>
                  Last name
                  <Input
                    value={form.lastName}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, lastName: event.target.value }))
                    }
                  />
                </Field>
              </Grid>

              <Grid>
                <Field>
                  Email
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, email: event.target.value }))
                    }
                  />
                </Field>
                <Field>
                  Phone (optional)
                  <Input
                    value={form.contactPhone}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, contactPhone: event.target.value }))
                    }
                  />
                </Field>
              </Grid>

              <Grid>
                <Field>
                  Address
                  <Input
                    value={form.addressLine1}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, addressLine1: event.target.value }))
                    }
                  />
                </Field>
                <Field>
                  Zip code
                  <Input
                    value={form.postalCode}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, postalCode: event.target.value }))
                    }
                  />
                </Field>
              </Grid>

              <Grid>
                <Field>
                  City
                  <Input
                    value={form.city}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, city: event.target.value }))
                    }
                  />
                </Field>
                <Field>
                  State
                  <Input
                    value={form.state}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, state: event.target.value }))
                    }
                  />
                </Field>
              </Grid>

              <Grid>
                <Field>
                  Were you referred? (optional)
                  <Select
                    value={form.wasReferred}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        wasReferred: event.target.value as 'no' | 'yes',
                        referredBy:
                          event.target.value === 'yes' ? prev.referredBy : '',
                      }))
                    }
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Select>
                </Field>
                {form.wasReferred === 'yes' ? (
                  <Field>
                    Who referred you?
                    <Input
                      value={form.referredBy}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, referredBy: event.target.value }))
                      }
                      placeholder="Referral name"
                    />
                  </Field>
                ) : null}
              </Grid>

              <Grid>
                <Field>
                  LinkedIn (optional)
                  <Input
                    value={form.linkedInUrl}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, linkedInUrl: event.target.value }))
                    }
                  />
                </Field>
                <Field>
                  Portfolio (optional)
                  <Input
                    value={form.portfolioUrl}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, portfolioUrl: event.target.value }))
                    }
                  />
                </Field>
              </Grid>

              <Field>
                Upload resume
                <FileInput
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      resumeFile: event.target.files?.[0] ?? null,
                    }))
                  }
                />
              </Field>

              <Field>
                Upload cover letter
                <FileInput
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      coverLetterFile: event.target.files?.[0] ?? null,
                    }))
                  }
                />
              </Field>

              <Field>
                Cover letter notes (optional)
                <Textarea
                  value={form.coverLetterText}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, coverLetterText: event.target.value }))
                  }
                  placeholder="Share why this role is a fit."
                />
              </Field>

              <ConsentField>
                <ConsentCheckbox
                  type="checkbox"
                  checked={form.backgroundCheckAcknowledged}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      backgroundCheckAcknowledged: event.target.checked,
                    }))
                  }
                />
                I acknowledge that Cybard will conduct an official background check as part of the hiring process.
              </ConsentField>

              {error ? <ErrorText>{error}</ErrorText> : null}

              <Submit
                type="submit"
                disabled={submitApplication.isPending}
              >
                {submitApplication.isPending ? 'Submitting...' : 'Submit application'}
              </Submit>
            </Form>
          </Card>
        </Layout>
      </Container>
    </Page>
  );
}
